import React from "react";
import { motion } from "framer-motion";
import { Rocket, CheckCircle } from 'lucide-react';
import { processSteps, myriadeColors, services, getServiceGradient } from "./data";

const ProcessSteps = () => {
    return (
        <section className="relative py-40 overflow-hidden">
            {/* Animated particles */}
            <div className="absolute inset-0 overflow-hidden">
                {[...Array(10)].map((_, i) => (
                    <motion.div
                        key={i}
                        className="absolute w-2 h-2 bg-purple-400/20 rounded-full"
                        animate={{
                            y: [0, -100, 0],
                            x: [0, Math.random() * 50 - 25, 0],
                        }}
                        transition={{
                            duration: 4 + Math.random() * 3,
                            repeat: Infinity,
                            delay: i * 0.5
                        }}
                        style={{
                            left: `${Math.random() * 100}%`,
                            top: `${Math.random() * 100}%`,
                        }}
                    />
                ))}
            </div>

            <div className="relative z-10 max-w-7xl mx-auto px-4">
                {/* Section Header */}
                <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="text-center mb-20"
                >
                    <div className="inline-flex flex-col md:flex-row items-center gap-4 mb-6 px-4">
                        <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                            className="w-12 h-12 md:w-16 md:h-16 rounded-full flex items-center justify-center shrink-0"
                            style={{
                                border: `2px solid ${myriadeColors.blue}20`,
                                background: getServiceGradient(services[0])
                            }}
                        >
                            <Rocket className="w-6 h-6 md:w-8 md:h-8 text-white" />
                        </motion.div>

                        <h2 className="text-4xl sm:text-5xl md:text-7xl font-bold text-white text-center">
                            Notre<span className="bg-gradient-to-r from-blue-400 via-cyan-300 to-blue-600 bg-clip-text text-transparent px-2 sm:px-4">Processus</span>
                        </h2>
                    </div>

                    <p className="text-base sm:text-xl text-gray-300 max-w-2xl mx-auto px-6">
                        Une méthodologie éprouvée pour transformer vos idées en solutions concrètes
                    </p>
                </motion.div>

                {/* Process Timeline */}
                <div className="relative">
                    {/* Vertical timeline line */}
                    <motion.div
                        initial={{ height: 0 }}
                        whileInView={{ height: "100%" }}
                        transition={{ duration: 1.5, ease: "easeInOut" }}
                        className="absolute left-8 md:left-1/2 top-0 -translate-x-1/2 w-1"
                        style={{
                            background: `linear-gradient(to bottom, ${myriadeColors.gradientBlue[0]}, ${myriadeColors.green})`
                        }}
                    />

                    {/* Process Steps */}
                    {processSteps.map((step, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8, delay: index * 0.2 }}
                            viewport={{ once: true }}
                            className={`relative flex flex-col md:flex-row ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} items-start md:items-center gap-8 md:gap-12 mb-20 md:mb-32 last:mb-0 px-4 md:px-0`}
                        >
                            {/* Step Content */}
                            <div className={`w-full md:w-1/2 pl-16 md:pl-0 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                                <motion.div
                                    whileHover={{
                                        scale: 1.02,
                                        y: -5
                                    }}
                                    className="relative bg-gray-900/50 backdrop-blur-xl rounded-2xl md:rounded-3xl p-6 md:p-8 border border-gray-800 shadow-xl"
                                >
                                    <div className="flex flex-col md:flex-row items-start md:items-center gap-4 mb-6">
                                        <motion.div
                                            animate={{
                                                scale: [1, 1.1, 1],
                                                rotate: [0, 5, -5, 0]
                                            }}
                                            transition={{
                                                duration: 4,
                                                repeat: Infinity,
                                                repeatDelay: 2
                                            }}
                                            className="w-12 h-12 md:w-16 md:h-16 rounded-xl md:rounded-2xl flex items-center justify-center shrink-0"
                                            style={{
                                                background: `linear-gradient(135deg, ${step.color}, ${step.color}dd)`,
                                                boxShadow: `0 10px 30px ${step.color}40`
                                            }}
                                        >
                                            {React.createElement(step.icon, { className: "w-6 h-6 md:w-8 md:h-8 text-white" })}
                                        </motion.div>

                                        <div className="flex-1">
                                            <span className="text-xs md:text-sm font-medium px-3 py-1 rounded-full mb-2 inline-block"
                                                style={{
                                                    background: step.color + '15',
                                                    color: step.color,
                                                    border: `1px solid ${step.color}30`
                                                }}>
                                                {step.duration}
                                            </span>

                                            <h3 className="text-xl md:text-3xl font-bold text-white mb-3 md:mb-4">
                                                {step.title}
                                            </h3>

                                            <p className="text-sm md:text-gray-300 mb-6">
                                                {step.description}
                                            </p>

                                            <div className="space-y-2 md:space-y-3">
                                                {step.features.map((feature, idx) => (
                                                    <motion.div
                                                        key={idx}
                                                        initial={{ opacity: 0, x: -20 }}
                                                        whileInView={{ opacity: 1, x: 0 }}
                                                        transition={{ delay: idx * 0.1 }}
                                                        className="flex items-center gap-3 p-2 md:p-3 rounded-xl"
                                                        style={{
                                                            background: step.color + '10',
                                                            border: `1px solid ${step.color}20`
                                                        }}
                                                    >
                                                        <CheckCircle className="w-4 h-4 md:w-5 md:h-5 shrink-0"
                                                            style={{ color: step.color }} />
                                                        <span className="text-xs md:text-[15px] md:text-gray-200">{feature}</span>
                                                    </motion.div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Decorative element */}
                                    <motion.div
                                        animate={{
                                            opacity: [0.1, 0.3, 0.1],
                                            scale: [1, 1.2, 1]
                                        }}
                                        transition={{ duration: 3, repeat: Infinity }}
                                        className="absolute -top-4 -right-4 w-24 h-24 rounded-full blur-xl"
                                        style={{
                                            background: `radial-gradient(circle, ${step.color}20, transparent 70%)`
                                        }}
                                    />
                                </motion.div>
                            </div>

                            {/* Timeline Point */}
                            <div className="absolute left-8 md:left-1/2 top-0 md:top-1/2 -translate-x-1/2 md:-translate-y-1/2 z-10">
                                <motion.div
                                    initial={{ scale: 0 }}
                                    whileInView={{ scale: 1 }}
                                    transition={{
                                        type: "spring",
                                        stiffness: 200,
                                        damping: 15,
                                        delay: index * 0.1
                                    }}
                                    className="w-12 h-12 md:w-16 md:h-16 rounded-full flex items-center justify-center shadow-lg"
                                    style={{
                                        background: `linear-gradient(135deg, ${step.color}, ${step.color}dd)`,
                                        boxShadow: `0 0 40px ${step.color}40`
                                    }}
                                >
                                    <div className="absolute inset-0 rounded-full bg-gradient-to-br from-white/20 to-transparent" />
                                    {React.createElement(step.icon, { className: "w-6 h-6 md:w-8 md:h-8 text-white relative z-10" })}

                                    {/* Pulsing animation */}
                                    <motion.div
                                        initial={{ scale: 0, opacity: 1 }}
                                        animate={{ scale: 2, opacity: 0 }}
                                        transition={{
                                            duration: 2,
                                            repeat: Infinity,
                                            repeatDelay: 1
                                        }}
                                        className="absolute inset-0 rounded-full border-2"
                                        style={{ borderColor: step.color + '60' }}
                                    />
                                </motion.div>
                            </div>

                            {/* Spacer for alternation on desktop */}
                            <div className="hidden md:block md:w-1/2"></div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default ProcessSteps;
